import { Typography } from '@mui/material';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  DialPadIcon,
  DotsIcon,
  EmergencyIcon,
  HandoffIcon,
  MicroIcon,
  TeamingIcon,
  TextInputIcon,
  VideoRecorderIcon,
} from 'assets/svg';
import { VrsCallCommentsButton } from 'features/call/vrs-call-comments/components';
import { BugReportReason } from 'features/call/vrs-call-comments/enums';
import type { VrsCallCommentsBugReportForm } from 'features/call/vrs-call-comments/interfaces';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';

interface VrsCallCommentsCategoriesControlProps {
  changeCategoryHandler: (bugReportReasons: Set<string>) => void;
  control: Control<VrsCallCommentsBugReportForm>;
}

export const VrsCallCommentsCategoriesControl: FC<
  VrsCallCommentsCategoriesControlProps
> = ({ changeCategoryHandler, control }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'bugReport',
  });
  const [selectedCategories, setSelectedCategories] = useState(new Set(''));
  const handleButtonClick = useCallback(
    (bugReportReason: BugReportReason, onChange: (value: string[]) => void) => {
      const currentValuesList = new Set(selectedCategories);
      if (currentValuesList.has(bugReportReason)) {
        currentValuesList.delete(bugReportReason);
      } else {
        currentValuesList.add(bugReportReason);
      }

      setSelectedCategories(currentValuesList);
      changeCategoryHandler(currentValuesList);
      onChange([...currentValuesList]);
    },
    [changeCategoryHandler, selectedCategories]
  );

  return (
    <>
      <Typography
        variant='subtitle1'
        color={theme.palette.grey[300]}
        mb={4}
        mt={16}
      >
        {t('categoriesTitle')}
      </Typography>
      <StyledButtonWrapper>
        <Controller
          name='categories'
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange } }) => (
            <>
              <VrsCallCommentsButton
                icon={<VideoRecorderIcon />}
                title={t(BugReportReason.VIDEO)}
                onClick={() =>
                  handleButtonClick(BugReportReason.VIDEO, onChange)
                }
                isChecked={selectedCategories.has(BugReportReason.VIDEO)}
              />
              <VrsCallCommentsButton
                icon={<MicroIcon />}
                title={t(BugReportReason.AUDIO)}
                onClick={() =>
                  handleButtonClick(BugReportReason.AUDIO, onChange)
                }
                isChecked={selectedCategories.has(BugReportReason.AUDIO)}
              />
              <VrsCallCommentsButton
                icon={<TextInputIcon />}
                title={t(BugReportReason.CALLER_TEXT)}
                onClick={() =>
                  handleButtonClick(BugReportReason.CALLER_TEXT, onChange)
                }
                isChecked={selectedCategories.has(BugReportReason.CALLER_TEXT)}
              />
              <VrsCallCommentsButton
                icon={<EmergencyIcon />}
                title={t(BugReportReason.EMERGENCY)}
                onClick={() =>
                  handleButtonClick(BugReportReason.EMERGENCY, onChange)
                }
                isChecked={selectedCategories.has(BugReportReason.EMERGENCY)}
              />
              <VrsCallCommentsButton
                icon={<DialPadIcon />}
                title={t(BugReportReason.DIALPAD)}
                onClick={() =>
                  handleButtonClick(BugReportReason.DIALPAD, onChange)
                }
                isChecked={selectedCategories.has(BugReportReason.DIALPAD)}
              />
              <VrsCallCommentsButton
                icon={<TeamingIcon />}
                title={t(BugReportReason.TEAMING)}
                onClick={() =>
                  handleButtonClick(BugReportReason.TEAMING, onChange)
                }
                isChecked={selectedCategories.has(BugReportReason.TEAMING)}
              />
              <VrsCallCommentsButton
                icon={<HandoffIcon />}
                title={t(BugReportReason.HANDOFF)}
                onClick={() =>
                  handleButtonClick(BugReportReason.HANDOFF, onChange)
                }
                isChecked={selectedCategories.has(BugReportReason.HANDOFF)}
              />
              <VrsCallCommentsButton
                icon={<DotsIcon />}
                title={t(BugReportReason.OTHER)}
                onClick={() =>
                  handleButtonClick(BugReportReason.OTHER, onChange)
                }
                isChecked={selectedCategories.has(BugReportReason.OTHER)}
              />
            </>
          )}
        />
      </StyledButtonWrapper>
    </>
  );
};

const StyledButtonWrapper = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  & > * {
    margin-bottom: ${theme.spacing(8)};
    path {
      stroke: ${theme.palette.common.white};
    }
  }
`;
